import React, { useState } from "react";
import { Linkedin, Youtube, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Image } from "@/components/ui/image";
import CalendlyModal from "@/components/tchatix/CalendlyModal";

const LOGO = "https://media.base44.com/images/public/6a69bb99139c43be5222264c/c922d2e1f_Tchatix.png";

const COLS = [
  {
    key: "product",
    items: [
      { key: "platform", to: "/" },
      { key: "agents", to: "/simulateur" },
      { key: "builder", to: "/agent-builder" },
      { key: "orchestration", to: "/simulateur" },
      { key: "simulator", to: "/simulateur" },
      { key: "tarifs", to: "/tarifs" }
    ]
  },
  {
    key: "solutions",
    items: [
      { key: "esn", demo: true },
      { key: "engineering", demo: true },
      { key: "ecommerce", demo: true },
      { key: "health", demo: true },
      { key: "legal", demo: true },
      { key: "finance", demo: true },
      { key: "industry", demo: true },
      { key: "all", demo: true }
    ]
  },
  {
    key: "resources",
    items: [
      { key: "blog", to: "/blog" },
      { key: "press", to: "/contact" },
      { key: "simulator", to: "/simulateur" },
      { key: "bfm", external: "https://www.bfmtv.com/economie/replay-emissions/le-pitch/video-le-pitch-tchatix-des-agents-ia-personnalises-07-07_VN-202607070139.html" },
      { key: "ovh", to: "/securite-rgpd" },
      { key: "france2030", to: "/a-propos" }
    ]
  },
  {
    key: "company",
    items: [
      { key: "about", to: "/a-propos" },
      { key: "security_rgpd", to: "/securite-rgpd" },
      { key: "securite_ia", to: "/securite-ia" },
      { key: "contact", to: "/contact" },
      { key: "cookies", to: "/cookies" },
      { key: "privacy", to: "/confidentialite" },
      { key: "recrutement", to: "/recrutement" },
      { key: "demo", demo: true }
    ]
  }
];


const SOCIALS = [
  { icon: <Youtube className="w-4 h-4" />, href: "https://www.youtube.com", label: "Tchatix sur YouTube" },
  { icon: <Linkedin className="w-4 h-4" />, href: "https://www.linkedin.com", label: "Tchatix sur LinkedIn" },
  { icon: <Instagram className="w-4 h-4" />, href: "https://www.instagram.com", label: "Tchatix sur Instagram" },
  { icon: <Mail className="w-4 h-4" />, href: "mailto:contact@tchatix.com", label: "Contacter Tchatix par email" }
];


function renderItem(item, label, openDemo) {
  if (item.demo) {
    return (
      <button
        data-cta-calendly="footer"
        onClick={openDemo}
        className="block py-1 text-left hover:text-white transition-colors cursor-pointer"
      >
        {label}
      </button>
    );
  }
  if (item.to) {
    return (
      <Link to={item.to} className="block py-1 hover:text-white transition-colors">
        {label}
      </Link>
    );
  }
  if (item.external) {
    return (
      <a href={item.external} target="_blank" rel="noopener noreferrer" className="block py-1 hover:text-white transition-colors">
        {label}
      </a>
    );
  }
  return <span className="block py-1 text-[#90A0B2]">{label}</span>;
}

export default function Footer() {
  const { t } = useTranslation();
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);

  return (
    <>
      <footer className="bg-[#001D31] text-white">
        <div className="max-w-7xl mx-auto px-8 py-14">
          {/* ── Top grid ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Logo cell */}
            <div className="md:flex flex-col items-center justify-center gap-3 hidden md:flex h-full">
              <Image src={LOGO} fittingType="fit" alt="Tchatix" className="w-16 h-16 bg-white rounded-2xl p-2" />
              <span className="font-semibold text-white [font-family:'Poppins',_sans-serif]">Tchatix.AI</span>
            </div>

            {/* 4 columns with dividers */}
            {COLS.map((col) =>
              <div key={col.key} className="md:pl-6 md:border-l md:border-white/10">
                <h3 className="text-[11px] uppercase tracking-widest font-medium text-[#6B7B8E] mb-4 [font-family:'Poppins',_sans-serif]">
                  {t(`footer.cols.${col.key}.header`)}
                </h3>
                <ul className="text-[11px] uppercase tracking-wider text-[#90A0B2] [font-family:'Poppins',_sans-serif]">
                  {col.items.map((item, i) =>
                    <li key={i}>{renderItem(item, t(`footer.cols.${col.key}.items.${item.key}`), openDemo)}</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Mobile logo */}
          <div className="flex md:hidden items-center gap-2 mb-8 pb-8 border-b border-white/10">
            <Image src={LOGO} fittingType="fit" alt="Tchatix" className="w-10 h-10 bg-white rounded-xl p-1.5" />
            <span className="font-semibold text-white [font-family:'Poppins',_sans-serif]">Tchatix.AI</span>
          </div>

          {/* ── Bottom bar ── */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-5">
            {/* Social */}
            <div className="flex items-center gap-4">
              {SOCIALS.map((s, i) =>
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#90A0B2] hover:text-white transition-colors [font-family:'Poppins',_sans-serif]">
                  {s.icon}
                  <span>{s.label.replace(/^Tchatix sur /, '').replace(/^Contacter /, '')}</span>
                </a>
              )}
            </div>

            {/* Description + copyright */}
            <p className="text-[11px] uppercase tracking-wider text-[#6B7B8E] [font-family:'Poppins',_sans-serif] text-center md:text-right">
              {t("footer.copyright", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>

      <CalendlyModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}